import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { Member, MemberEligibility, MemberLink } from '../../member/model/member';
import { MemberCheck } from '../model/memberCheck';
import { MessengerService } from './messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';

@Injectable({
  providedIn: 'root'
})
export class MemberService implements OnDestroy
{
  /**
   * Localized demographics.
   */
  public demographics: Array<{ text: string; value: string; key: string }> = [
    { text: 'First Name', value: 'FIRST_NAME', key: 'FIRST-NAME' },
    { text: 'Last Name', value: 'LAST_NAME', key: 'LAST-NAME' },
    { text: 'Date of Birth', value: 'DATE_OF_BIRTH', key: 'DATE-OF-BIRTH' },
    { text: 'Gender', value: 'GENDER', key: 'GENDER' },
    { text: 'Administrative Gender', value: 'ADMINISTRATIVE_GENDER', key: 'ADMINISTRATIVE-GENDER' },
    { text: 'Address Line 1', value: 'ADDRESS_LINE1', key: 'STREET' },
    { text: 'City', value: 'CITY', key: 'CITY' },
    { text: 'County', value: 'COUNTY', key: 'COUNTY' },
    { text: 'State', value: 'STATE', key: 'STATE' },
    { text: 'Zip/Postal Code', value: 'ZIP_POSTAL_CODE', key: 'POSTAL-CODE' },
    { text: 'Country', value: 'COUNTRY', key: 'COUNTRY' },
    { text: 'Home Phone', value: 'HOME_PHONE', key: 'HOME-PHONE' },
    { text: 'Cell Phone', value: 'CELL_PHONE', key: 'CELL-PHONE' },
    { text: 'Work Phone', value: 'WORK_PHONE', key: 'WORK-PHONE-LABEL' },
    { text: 'Other Phone', value: 'OTHER_PHONE', key: 'OTHER-PHONE-LABEL' },
    { text: 'Pager', value: 'PAGER', key: 'PAGER' },
    { text: 'Fax or Other', value: 'FAX', key: 'FAX-OR-OTHER' },
    { text: 'Website', value: 'URL', key: 'URL' },
    { text: 'Email', value: 'EMAIL', key: 'EMAIL' },
    { text: 'Notification Language', value: 'NOTIFICATION_LANGUAGE', key: 'NOTIFICATION-LANGUAGE' },
    { text: 'Preferred Notification', value: 'NOTIFICATION', key: 'NOTIFICATION' },
    { text: 'Video Provider', value: 'VIDEO_PROVIDER', key: 'PREFERRED-VIDEO-PROVIDER' },
    { text: 'Race/Ethnicity', value: 'RACE_ETHNICITIES', key: 'RACE-ETHNICITIES' },
    { text: 'Marital Status', value: 'MARITAL_STATUS', key: 'MARITAL-STATUS' },
    { text: 'Religious Affiliation', value: 'RELIGIOUS_AFFILIATION', key: 'RELIGIOUS-AFFILIATION' },
    { text: 'Education Level', value: 'EDUCATION_LEVEL', key: 'EDUCATION-LEVEL' },
    { text: 'Employment Status', value: 'EMPLOYMENT_STATUS', key: 'EMPLOYMENT-STATUS' },
    { text: 'Preferred Name', value: 'PREFERRED_NAME', key: 'PREFERRED-NAME' },
    { text: 'Veteran Status', value: 'VETERAN_STATUS', key: 'VETERAN-STATUS' },
    { text: 'Family Income', value: 'FAMILY_INCOME', key: 'FAMILY-INCOME' },
    { text: 'Family Income Unknown/Declined', value: 'FAMILY_INCOME_UNKNOWN_DECLINED', key: 'FAMILY-INCOME-UNKNOWN-DECLINED' },
    { text: 'Housing', value: 'HOUSING', key: 'HOUSING-STATUS' },
    { text: 'Household Size', value: 'HOUSEHOLD_SIZE', key: 'HOUSEHOLD-SIZE' },
    { text: 'Dependents', value: 'DEPENDENTS', key: 'DEPENDENTS' },
    { text: 'Insurance', value: 'MEDICAL_INSURANCE', key: 'MEDICAL-INSURANCE' },
    { text: 'Spoken Languages', value: 'SPOKEN_LANGUAGES', key: 'SPOKEN-LANGUAGES' },
    { text: 'Written Languages', value: 'WRITTEN_LANGUAGES', key: 'WRITTEN-LANGUAGES' },
    { text: 'Reading Languages', value: 'READING_LANGUAGES', key: 'READING-LANGUAGES' },
    { text: 'Pronouns', value: 'NAME_PRONOUN', key: 'NAME-PRONOUN' },
    { text: 'Pronouns (Other)', value: 'NAME_PRONOUN_OTHER', key: 'NAME-PRONOUN-OTHER' },
    { text: 'English Proficiency', value: 'ENGLISH_PROFICIENCY', key: 'ENGLISH-PROFICIENCY' },
    { text: 'Interpreter Need', value: 'INTERPRETER_NEED', key: 'INTERPRETER-NEED' },
    { text: 'Interpreter Need (Other)', value: 'INTERPRETER_NEED_OTHER', key: 'INTERPRETER-NEED-OTHER' },
    { text: 'Lives Alone', value: 'LIVES_ALONE', key: 'LIVES-ALONE' },
    { text: 'Race Detail', value: 'RACES', key: 'RACES' },
    { text: 'Sexual Orientation', value: 'SEXUAL_ORIENTATION', key: 'SEXUAL-ORIENTATION' },
    { text: 'Tribal Affiliated', value: 'TRIBAL_AFFILIATED', key: 'TRIBAL-AFFILIATED' },
    { text: 'Tribal Affiliations', value: 'TRIBAL_AFFILIATIONS', key: 'TRIBAL-AFFILIATIONS' },
    { text: 'Tribal Enrolled', value: 'TRIBAL_ENROLLED', key: 'TRIBAL-ENROLLED' },
    { text: 'Tribal Enrollments', value: 'TRIBAL_ENROLLMENTS', key: 'TRIBAL-ENROLLMENTS' },
    { text: 'Total Children', value: 'TOTAL_CHILDREN', key: 'TOTAL-CHILDREN' },
    { text: 'Total Adults', value: 'TOTAL_ADULTS', key: 'TOTAL-ADULTS' },
    { text: 'Enhanced Service Eligibility', value: 'ENHANCED_SERVICE_ELIGIBILITY', key: 'ENHANCED-SERVICE-ELIGIBILITY' },
    { text: 'Preferred Contact Methods', value: 'PREFERRED_CONTACT_METHODS', key: 'PREFERRED-CONTACT-METHODS' },
    { text: 'Primary Caregiver', value: 'PRIMARY_CAREGIVER', key: 'PRIMARY-CAREGIVER' },
    { text: 'First Name (Other Names)', value: 'FIRST_NAME_OTHER_NAMES', key: 'FIRST-NAME-OTHER-NAMES' },
    { text: 'Last Name (Other Names)', value: 'LAST_NAME_OTHER_NAMES', key: 'LAST-NAME-OTHER-NAMES' },
    { text: 'Middle Name', value: 'MIDDLE_NAME', key: 'MIDDLE-NAME' },
    { text: 'Work Extension', value: 'WORK_EXTENSION', key: 'WORK-EXTENSION' },
    { text: 'Address Line 2', value: 'ADDRESS_LINE2', key: 'STREET2' },
    { text: 'Poverty Level', value: 'POVERTY_LEVEL', key: 'POVERTY-LEVEL' },
  ];

  /**
   * Localized Community Resources.
   */
  public communityResources: Array<{ text: string; value: string; key: string; searchable: boolean }> = [
    { text: 'Organization', value: 'ORGANIZATION', key: 'ORGANIZATION', searchable: true },
    { text: 'Submitting Organization', value: 'SUBMITTING_ORGANIZATION', key: 'SUBMITTING-ORGANIZATION', searchable: true },
    { text: 'Status', value: 'STATUS', key: 'STATUS', searchable: true },
    { text: 'Service Category', value: 'SERVICE_CATEGORY', key: 'SERVICE-CATEGORY', searchable: true },
    { text: 'Channel', value: 'CHANNEL', key: 'CHANNEL', searchable: false },
    { text: 'Name', value: 'NAME', key: 'NAME', searchable: true },
    { text: 'Alternate Resource Name', value: 'ALIAS', key: 'ALIAS', searchable: false },
    { text: 'Description', value: 'DESCRIPTION', key: 'DESCRIPTION', searchable: false },
    { text: 'Eligibility', value: 'ELIGIBILITY', key: 'ELIGIBILITY', searchable: false },
    { text: 'Cost', value: 'COST', key: 'COST', searchable: false },
    { text: 'Languages', value: 'LANGUAGES', key: 'LANGUAGES', searchable: true },
    { text: 'Cities Served', value: 'AREAS_SERVED_CITIES', key: 'AREAS-SERVED-CITIES', searchable: true },
    { text: 'Counties Served', value: 'AREAS_SERVED_COUNTIES', key: 'AREAS-SERVED-COUNTIES', searchable: true },
    { text: 'Zip Codes Served', value: 'AREAS_SERVED_ZIPCODES', key: 'AREAS-SERVED-ZIPCODES', searchable: true },
    { text: 'Address Line 1', value: 'STREET', key: 'STREET', searchable: false },
    { text: 'Address Line 2', value: 'ADDRESS_LINE_2', key: 'ADDRESS-LINE-2', searchable: false },
    { text: 'City', value: 'CITY', key: 'CITY', searchable: false },
    { text: 'State', value: 'STATE', key: 'STATE', searchable: false },
    { text: 'Zip/Postal Code', value: 'POSTAL_CODE', key: 'POSTAL-CODE', searchable: false },
    { text: 'Country', value: 'COUNTRY', key: 'COUNTRY', searchable: false },
    { text: 'Program Contact Name', value: 'PROGRAM_CONTACT_NAME', key: 'PROGRAM-CONTACT-NAME', searchable: false },
    { text: 'Program Phone Number', value: 'PROGRAM_CONTACT_PHONE', key: 'PROGRAM-CONTACT-PHONE', searchable: false },
    { text: 'Program Fax Number', value: 'PROGRAM_CONTACT_FAX', key: 'PROGRAM-CONTACT-FAX', searchable: false },
    { text: 'Program Email', value: 'PROGRAM_CONTACT_EMAIL', key: 'PROGRAM-CONTACT-EMAIL', searchable: false },
    { text: 'Program URL', value: 'PROGRAM_CONTACT_URL', key: 'PROGRAM-CONTACT-URL', searchable: false },
    { text: 'Referral Contact Name', value: 'REFERRAL_CONTACT_NAME', key: 'REFERRAL-CONTACT-NAME', searchable: false },
    { text: 'Referral Phone Number', value: 'REFERRAL_CONTACT_PHONE', key: 'REFERRAL-CONTACT-PHONE', searchable: false },
    { text: 'Referral Fax Number', value: 'REFERRAL_CONTACT_FAX', key: 'REFERRAL-CONTACT-FAX', searchable: false },
    { text: 'Referral Email', value: 'REFERRAL_CONTACT_EMAIL', key: 'REFERRAL-CONTACT-EMAIL', searchable: false },
    { text: 'Referral URL', value: 'REFERRAL_CONTACT_URL', key: 'REFERRAL-CONTACT-URL', searchable: false },
    { text: 'Referral Emergency Number', value: 'REFERRAL_CONTACT_EMERGENCY_NUMBER', key: 'REFERRAL-CONTACT-EMERGENCY-NUMBER', searchable: false },
    { text: 'Referral Extension', value: 'REFERRAL_CONTACT_EXTENSION', key: 'REFERRAL-CONTACT-EXTENSION', searchable: false },
    { text: 'Application Process', value: 'APPLICATION_PROCESS', key: 'APPLICATION-PROCESS', searchable: false },
    { text: 'Alerts', value: 'ALERT_DESCRIPTION', key: 'ALERT-DESCRIPTION', searchable: false },
    { text: 'Documentation Required', value: 'DOCUMENTATION_REQUIRED', key: 'DOCUMENTATION-REQUIRED', searchable: false },
    {
      text: 'Accepting Disabled {{patientLabel}}s', value: 'ACCEPTING_DISABLED_PATIENTS', key: 'ACCEPTING-DISABLED-PATIENTS',
      searchable: false
    },
    {
      text: 'Accepting Electronic Referrals', value: 'ACCEPTING_ELECTRONIC_REFERRALS', key: 'ACCEPTING-ELECTRONIC-REFERRALS',
      searchable: false
    },
    { text: 'LGBTQ', value: 'LGBTQ', key: 'LGBTQ', searchable: false },
    { text: 'Accepting New {{patientLabel}}s', value: 'ACCEPTING_NEW_PATIENTS', key: 'ACCEPTING-NEW-PATIENTS', searchable: true },
    { text: 'Accepting Self Referrals', value: 'ACCEPTING_SELF_REFERRALS', key: 'ACCEPTING-SELF-REFERRALS', searchable: true },
    { text: 'Capacity', value: 'CAPACITY', key: 'CAPACITY', searchable: false },
    { text: 'Payment Methods', value: 'PAYMENT_METHODS', key: 'PAYMENT-METHODS', searchable: false },
    { text: 'Income Requirements', value: 'INCOME_REQUIREMENTS', key: 'INCOME-REQUIREMENTS', searchable: false },
    { text: 'Targeted Demographics', value: 'TARGETED_DEMOGRAPHICS', key: 'TARGETED-DEMOGRAPHICS', searchable: true },
    { text: 'Targeted Populations', value: 'TARGETED_POPULATIONS', key: 'TARGETED-POPULATIONS', searchable: true },
    { text: 'Taxonomies', value: 'TAXONOMIES', key: 'TAXONOMIES', searchable: true },
    { text: 'Licenses', value: 'LICENSES', key: 'LICENSES', searchable: false },
    {
      text: 'Directory Manager Name', value: 'DIRECTORY_MANAGER_NAME',
      key: 'DIRECTORY-MANAGER-NAME',
      searchable: false
    },
    {
      text: 'Directory Manager Email', value: 'DIRECTORY_MANAGER_EMAIL',
      key: 'DIRECTORY-MANAGER-EMAIL',
      searchable: false
    },
    {
      text: 'Enhanced Services Offered', value: 'ENHANCED_SERVICE_ELIGIBILITY', key: 'ENHANCED-SERVICES-OFFERED',
      searchable: true
    },
    { text: 'Able to Conduct Screenings', value: 'ABLE_TO_CONDUCT_SCREENINGS', key: 'ABLE-TO-CONDUCT-SCREENINGS', searchable: true },
    { text: 'Informational Only', value: 'INFORMATIONAL_ONLY', key: 'INFORMATIONAL-ONLY', searchable: true },
  ];

  /**
     * Localized Insurance Fields.
     */
  public insuranceFields: Array<{ text: string; value: string; key: string }> = [
    { text: 'Insurer', value: 'INSURER', key: 'INSURER' },
    { text: 'Start Date', value: 'START_DATE', key: 'START-DATE' },
    { text: 'End Date', value: 'END_DATE', key: 'END-DATE' },
    { text: 'Insured First Name', value: 'INSURED_FIRST_NAME', key: 'INSURED-FIRST-NAME' },
    { text: 'Insured Last Name', value: 'INSURED_LAST_NAME', key: 'INSURED-LAST-NAME' },
    { text: 'Insurer Phone', value: 'INSURER_PHONE', key: 'INSURER-BUSINESS-PHONE' },
    { text: 'Insurer Website', value: 'INSURER_WEBSITE', key: 'INSURER-WWW-URL' },
    { text: 'Member ID', value: 'INSURANCE_MEMBER_ID', key: 'INSURANCE-MEMBER-ID' },
    { text: 'Plan', value: 'PLAN', key: 'PLAN' },
    { text: 'Group ID', value: 'GROUP_ID', key: 'GROUP-ID' },
    { text: 'Policy Holder', value: 'POLICY_HOLDER', key: 'POLICY-HOLDER' },
    { text: 'Employer', value: 'EMPLOYER', key: 'EMPLOYER' },
    { text: 'Insurance Card', value: 'INSURANCE_CARD', key: 'INSURANCE-CARD' },
    { text: 'Insurance External ID', value: 'INSURANCE_EXTERNAL_ID', key: 'INSURANCE-EXTERNAL-ID' },
  ];

  public preferredContactMethods: Array<{ text: string; value: string; key: string; index: number }> = [
    { text: 'Home', value: 'HOME', key: 'HOME', index: 0 },
    { text: 'Cell', value: 'CELL', key: 'CELL', index: 1 },
    { text: 'Work', value: 'WORK', key: 'WORK-PHONE', index: 2 },
    { text: 'Other', value: 'OTHER', key: 'OTHER-PHONE', index: 3 },
    { text: 'Pager', value: 'PAGER', key: 'PAGER', index: 4 },
    { text: 'Email', value: 'EMAIL', key: 'EMAIL', index: 5 },
  ];

  public recordContact: Array<{ text: string; value: string; key: string }> = [
    { text: 'Method', value: 'METHOD', key: 'METHOD' },
    { text: 'Entity', value: 'ENTITY', key: 'ENTITY' },
    { text: 'Consent', value: 'CONSENT', key: 'CONSENT' },
    { text: 'Activities', value: 'ACTIVITIES', key: 'ACTIVITIES' },
    { text: 'Intensity', value: 'INTENSITY', key: 'INTENSITY' },
    { text: 'Mood', value: 'MOOD', key: 'SENTIMENT' },
  ];

  public readonly memberConsentGreen = '#669146';
  public readonly memberConsentRed = '#dc5d3f';
  public readonly memberConsentOrange = '#e99443';

  private messagingLanguageChangedSubscription: Subscription;

  /**
   * Constructor
   *
   * @param http Auto injected Angular HttpClient
   */
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private storageService: LocalStorageService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Members from REST service.
   */
  public getMembers(): Observable<Member[]>
  {
    return this.http.get<Member[]>('/members');
  }

  /**
   * Check eligibility for a specific member for a medicaid
   * @param memberId Member id
   * @returns MemberEligibility
   */
  public checkMemberEligibility(memberId: number): Observable<MemberEligibility>
  {
    const simulation = this.eligibilitySimulationMode() != null ? `?simulation=${this.eligibilitySimulationMode()}` : '';
    return this.http.get<MemberEligibility>(`/members/eligibility/${memberId}${simulation}`);
  }

  /**
   * Check the poverty level for the member.
   */
  public checkPovertyLevel(year: string, state: string, householdSize: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/members/poverty/level/${year}/${state}/${householdSize}`);
  }

  /**
   * Get a Member by ID from REST service.
   *
   * @param id Member ID
   */
  public getMember(id: number): Observable<Member>
  {
    return this.http.get<Member>(`/members/${id}`);
  }

  /**
   * Get a Member's Extensions by ID from REST service.
   *
   * @param id Member ID
  */
  public getMemberExtensions(id: number): Observable<any>
  {
    return this.http.get<any>(`/members/${id}/extensions`);
  }

  /**
   * Get a Member's Tasks by ID from REST service.
   *
   * @param caseId Member ID
  */
  public getMemberTasks(memberId: number, taskFilter: string): Observable<any>
  {
    return this.http.get<any>(`/members/${memberId}/tasks?filter=${taskFilter}`);
  }

  /**
   * Get a Member by ID from REST service.
   *
   * @param id Member ID
   */
  public getMemberMinimal(id: number): Observable<Member>
  {
    return this.http.get<Member>(`/members/${id}/minimal`);
  }

  /**
   * Get linked Member(s) by ID from REST service (proxy).
   *
   * @param id Member ID
   */
  public getLinkedMember(memberId): Observable<any[]>
  {
    return this.http.get<any[]>(`/members/linked/member/${memberId}`);
  }

  /**
   * Get a Member by MRN from REST service.
   *
   * @param id Member MRN
   */
  public getMemberByMRN(mrn: string): Observable<Member>
  {
    return this.http.get<Member>(`/members/MRN/${mrn}`);
  }

  /**
   * Save a Member.
   *
   * @param member Member to save.
   */
  public saveMember(member: any, isNew: boolean): Observable<any>
  {
    if (isNew)
    {
      return this.http.post<any>('/members', member);
    }
    else
    {
      return this.http.put('/members', member);
    }
  }

  /**
   * Get a Member to work by ID from REST service.
   *
   * @param id Member ID
   */
  public getMemberToWork(memberId: number, userId: number): Observable<Member>
  {
    return this.http.get<Member>(`/members/work/${memberId}/${userId}`);
  }

  /**
   * Get a Member to work by user ID from REST service.
   *
   * @param id Member ID
   */
  public getWorkMember(userId: number): Observable<Member>
  {
    return this.http.get<Member>(`/members/work/${userId}`);
  }

  /**
   * Set a member as tracked member.
   *
   * @param id Member ID
   */
  public trackMember(memberId: number): Observable<any>
  {
    return this.http.get<Member>(`/members/star/${memberId}`);
  }

  /**
   * Unset a member as tracked member.
   *
   * @param id Member ID
   */
  public untrackMember(memberId: number): Observable<any>
  {
    return this.http.get<Member>(`/members/unstar/${memberId}`);
  }

  /**
   * Get Members from REST service.
   */
  public getMemberEvents(memberId: number): Observable<any>
  {
    return this.http.get<any>(`/members/events/${memberId}`);
  }

  /**
   * Get Members from REST service.
   */
  public getMemberCountTabs(memberId: number): Observable<any>
  {
    return this.http.get<any>(`/members/tabs/count/${memberId}`);
  }

  /**
   * Send Consent.
   *
   * @param consent Consent to send.
   */
  public createConsent(consent: any): Observable<any>
  {
    return this.http.post<any>('/members/consent', consent);
  }

  /**
   * Request referrals history for the patient.
   *
   * @param memberId Member id
   */
  public requestReferralsHistory(memberId: any): Observable<any>
  {
    return this.http.post<any>(`/members/${memberId}/referrals/history`, null);
  }

  /**
   * Send Consent.
   *
   * @param consent Consent to send.
   */
  public revokeAllConsents(memberId: number): Observable<any>
  {
    return this.http.post<any>(`/members/consents/revoke/${memberId}/all`, null);
  }

  /**
   * Revoke a list of consents by ids.
   *
   * @param consentsToRevoke Consent ids to revoke.
   */
  public revokeSelectedConsents(consentsToRevoke: any): Observable<any>
  {
    return this.http.post('/members/consents/revoke', consentsToRevoke);
  }


  /**
   * Confirm Appointment.
   *
   * @param data Appointment id
   */
  public confirmConsent(data: any): Observable<any>
  {
    return this.http.put('/members/consent/confirm/email', data);
  }

  /**
   * Send Community invite.
   *
   * @param Invite Invite to send.
   */
  public createInvite(invite: any): Observable<any>
  {
    return this.http.post<any>('/members/invite', invite);
  }

  /**
   * Get member risks scores
   *
   * @param memberId Member Id
   */
  public getMemberRisks(memberId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/members/risks/${memberId}`);
  }

  /**
   * Get member risks history scores
   *
   * @param memberId Member Id
   * @param serviceTypeId Service Type Id
   */
  public getMemberRisksHistory(memberId: number, serviceTypeId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/members/risks/audit/${memberId}/type/${serviceTypeId}`);
  }

  /**
   * Get member consent info
   *
   * @param memberId Member Id
   */
  public getMemberConsentInfo(memberId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/members/${memberId}/consent`);
  }

  /**
   * Get member invite info. This returns the MemberDTO object.
   *
   * @param memberId Member Id
   * @returns Member Id object.
   */
  public getMemberInviteInfo(memberId: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/members?page=0&size=1&search=memberId:eq:%22${memberId}%22:and`);
  }

  /**
   * Convert a list of Subject Identifiers to texts.
   *
   * @param views List of Subject Identifiers.
   */
  public convertSubjectIdentifiersToDisplayTexts(allIdentifiers: any[], identifiers: string): string
  {
    const listIdentifiers = identifiers.split(',');
    const identifiersText = [];
    listIdentifiers.forEach(identifier =>
    {
      const identifierText = this.getSubjectIdentifierText(allIdentifiers, identifier);
      if (identifierText !== '')
      {
        identifiersText.push(identifierText);
      }
    });
    return identifiersText.join();
  }

  /**
 * Convert a list of Resource Statuses to texts.
 *
 * @param views List of Resource Statuses.
 */
  public convertResourceStatusesToDisplayTexts(allStatuses: any[], status: string): string
  {
    const listStatuses = status.split(',');
    const statusesText = [];
    listStatuses.forEach(status =>
    {
      const statusText = this.getSubjectIdentifierText(allStatuses, status); // getSubjectIdentifierText works on both
      if (statusText !== '')
      {
        statusesText.push(statusText);
      }
    });
    return statusesText.join();
  }

  /**
 * Convert a list of Code Sets to texts.
 *
 * @param views List of Code Sets.
 */
  public convertCodeSetsToDisplayTexts(allCodeSets: any[], codeSets: string): string
  {
    const listCodeSets = codeSets.split(',');
    const codeSetsText = [];
    listCodeSets.forEach(codeSet =>
    {
      const codeSetText = this.getCodeSetText(allCodeSets, codeSet);
      if (codeSetText !== '')
      {
        codeSetsText.push(codeSetText);
      }
    });
    return codeSetsText.join();
  }

  /**
   * Get the Subject Identifier text to display.
   *
   * @param identifier Scheduler view value.
   */
  public getSubjectIdentifierText(allIdentifiers: any[], identifier: string): string
  {
    let text = '';
    const identifierObject = allIdentifiers.find(x => x.id === identifier);
    if (identifierObject !== undefined)
    {
      text = identifierObject.displayName;
    }
    return text;
  }

  /**
 * Get the Code set text to display.
 *
 * @param identifier Scheduler view value.
 */
  public getCodeSetText(allCodeSets: any[], codeSet: string): string
  {
    let text = '';
    const codeSetObject = allCodeSets.find(x => x.id === codeSet);
    if (codeSetObject !== undefined)
    {
      text = codeSetObject.displayName;
    }
    return text;
  }

  /**
   * Check if member exists for the first/last name, middle name and DOB
   *
   * @param existsMember Member check data
   */
  public memberExists(existsMember: MemberCheck): Observable<any>
  {
    return this.http.post<any>('/members/exists', existsMember);
  }

  /**
   * Merge a member
   *
   * @param mergeRequest Merge object
   */
  public mergeMember(mergeRequest: any): Observable<any>
  {
    return this.http.post<any>('/members/merge', mergeRequest);
  }

  /**
   * Check if the source patient has an open batch
   *
   * @param memberId Source Patient Id
   */
  public hasOpenBatch(sourceMemberId: number): Observable<any>
  {
    return this.http.get(`/members/hasOpenBatch?sourceMemberId=${sourceMemberId}`);
  }

  /**
   * Get the duplicated Members.
   */
  public getDuplicatedMembers(): Observable<Member[]>
  {
    return this.http.get<Member[]>('/members/duplicates');
  }

  /**
   * Convert a list of required demographics to texts.
   *
   * @param demographics List of Demographics.
   */
  public convertDemographicsToDisplayTexts(demographics: string): string
  {
    const demographicsText = [];
    if (demographics.trim() !== '')
    {
      const res = demographics.split(',');
      res.forEach(demographic =>
      {
        const demographicText = this.getDemographicText(demographic);
        if (demographicText !== '')
        {
          demographicsText.push(demographicText);
        }
      });
    }
    return demographicsText.join();
  }

  public convertRecordContactToDisplayTexts(recordContact: any): string
  {
    const res = recordContact.split(',');

    res.forEach(contact =>
    {
      const fieldName = contact.split('|')[0];
      const contactText = this.getRecordContactText(fieldName);
      recordContact = recordContact.replace(fieldName, contactText);
    });
    return recordContact.replaceAll('|true', ' (' + this.translate.instant('FIELD-REQUIRED').toLowerCase() + ')').replaceAll('|false', ' (' + this.translate.instant('FIELD-NOT-REQUIRED').toLowerCase() + ')').replaceAll(',', ', ');
  }

  /**
   * Convert a list of preferred contact methods to texts.
   *
   * @param communityResources List of CommunityResources.
   */
  public convertPreferredContactMethodsToDisplayTexts(methods: string): string 
  {
    const res = methods.split(',');
    const methodsText = [];
    res.forEach(method =>
    {
      const methodText = this.getPreferredContactMethodText(method);
      if (methodText !== '')
      {
        methodsText.push(methodText);
      }
    });
    return methodsText.join();
  }

  /**
   * Get the Preferred Contact method text to display.
   *
   * @param method Preferred Contact Method string.
   */
  public getPreferredContactMethodText(method: string): string
  {
    let text = method;
    // Either match on value (HOME,CELL) or index (0,1)
    const methodObject = this.preferredContactMethods.find(x => (x.value === method || x.index === parseInt(method, 10)));
    if (methodObject !== undefined)
    {
      text = methodObject.text;
    }
    return text;
  }

  /**
   * Convert a list of required Community Resources to texts.
   *
   * @param communityResources List of CommunityResources.
   */
  public convertCommunityResourcesToDisplayTexts(communityResources: string): string
  {
    const res = communityResources.split(',');
    const communityResourcesText = [];
    res.forEach(communityResource =>
    {
      const communityResourceText = this.getCommunityResourceText(communityResource);
      if (communityResourceText !== '')
      {
        communityResourcesText.push(communityResourceText);
      }
    });
    return communityResourcesText.join();
  }

  /**
   * Convert a list of required Insurance Fields to texts.
   *
   * @param insuranceFields List of Insurance Fields.
   */
  public convertInsuranceFieldsToDisplayTexts(insuranceFields: string): string
  {
    const res = insuranceFields.split(',');
    const insuranceFieldsText = [];
    res.forEach(insuranceField =>
    {
      const insuranceFieldText = this.getInsuranceFieldText(insuranceField);
      if (insuranceFieldText !== '')
      {
        insuranceFieldsText.push(insuranceFieldText);
      }
    });
    return insuranceFieldsText.join();
  }

  /**
   * Get the Demographic text to display.
   *
   * @param demographic Demographic string.
   */
  public getDemographicText(demographic: string): string
  {
    let text = demographic;
    const demographicObject = this.demographics.find(x => x.value === demographic);
    if (demographic !== undefined)
    {
      text = demographicObject.text;
    }
    return text;
  }

  public getRecordContactText(contact: string): string
  {
    let text = contact;
    const recordContactObject = this.recordContact.find(x => x.value === contact);
    if (contact !== undefined)
    {
      text = recordContactObject.text;
    }
    return text;
  }

  /**
   * Get the Community Resource text to display.
   *
   * @param communityResource Community Resource string.
   */
  public getCommunityResourceText(communityResource: string): string
  {
    let text = communityResource;
    const communityResourceObject = this.communityResources.find(x => x.value === communityResource);
    if (communityResource !== undefined)
    {
      text = communityResourceObject.text;
    }
    return text;
  }

  /**
   * Get the Insurance Fields text to display.
   *
   * @param insuranceField Insurance Field string.
   */
  public getInsuranceFieldText(insuranceField: string): string
  {
    let text = insuranceField;
    const insuranceFieldObject = this.insuranceFields.find(x => x.value === insuranceField);
    if (insuranceField !== undefined)
    {
      text = insuranceFieldObject.text;
    }
    return text;
  }

  /**
   * Delete a pinned user provider program.
   *
   * @param memberId Member id
   * @param communityProviderProgramId Community Provider Program id
   */
  public deletePinnedCommunityProviderProgram(memberId: number, communityProviderProgramId: number): Observable<any>
  {
    return this.http.delete<any>(`/community/service/member/${memberId}/provider/program/${communityProviderProgramId}`);
  }

  /**
   * Save the community provider program save resource to the current logged in user.
   *
   * @param data Pinned Community Provider Programs ids.
   */
  public savePinnedResources(memberId: number, data: Array<number>): Observable<any>
  {
    return this.http.post(`/community/service/member/${memberId}/pin/resources`, data);
  }

  /**
   * Save Member recent viewed
   *
   * @param memberId Member Id
   */
  public saveRecentView(memberId: number): Observable<any>
  {
    return this.http.post(`/members/recent/${memberId}`, null);
  }

  /**
   * Clear all Members recent viewed
   */
  public clearRecentView(): Observable<any>
  {
    return this.http.post(`/members/recent/clear`, null);
  }

  /**
   * Add a Household Member
   */
  public addHouseholdMember(data: MemberLink): Observable<any>
  {
    return this.http.post('/members/link/member', data);
  }

  /**
   * Remove a Household Member
   */
  public removeHouseholdMember(memberId: number): Observable<any>
  {
    return this.http.delete<any>(`/members/unlink/${memberId}`);
  }

  /**
   * Check if the patient is member of a household
   */
  public isMemberPartOfAHousehold(id: number): Observable<any>
  {
    return this.http.get(`/members/linked/${id}`);
  }

  public eligibilitySimulationMode(): any 
  {
    return this.storageService.retrieve('eligibility.simulation');
  }

  public getFieldRequiredValue(recordContactSystemSetting: string, fieldName: string): boolean
  {
    const pairs = recordContactSystemSetting.split(',');

    for (const pair of pairs)
    {
      const [k, v] = pair.split('|');
      if (k === fieldName)
      {
        return v === 'true';
      }
    }
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    setTimeout(() =>
    {
      this.demographics.forEach((demographic) =>
      {
        this.translate.get(demographic.key).subscribe({
          next: (res) =>
          {
            demographic.text = res;
          }
        });
      });
      this.recordContact.forEach((contact) =>
      {
        this.translate.get(contact.key).subscribe({
          next: (res) =>
          {
            contact.text = res;
          }
        });
      });
      this.communityResources.forEach((communityResource) =>
      {
        this.translate.get(communityResource.key).subscribe({
          next: (res) =>
          {
            communityResource.text = res;
          }
        });
        this.communityResources = sortBy(this.communityResources, ['text']);
      });
      this.insuranceFields.forEach((insuranceField) =>
      {
        this.translate.get(insuranceField.key).subscribe({
          next: (res) =>
          {
            insuranceField.text = res;
          }
        });
      });
      this.preferredContactMethods.forEach((contactMethod) =>
      {
        this.translate.get(contactMethod.key).subscribe({
          next: (res) =>
          {
            contactMethod.text = res;
          }
        });
      });
    });
  }
}

